import {
  SearchAdminInvoiceReportsQueryPaymentMethod,
  SearchInvoiceReportsQueryMfFile,
  SearchInvoiceReportsQueryPaymentStatus,
  SearchInvoiceReportsQueryStatus,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import type { AdminInvoiceReportsSearchFormData } from '@/features/admin_invoice_reports/types';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const isValidStatus = (
  v: string | null
): v is SearchInvoiceReportsQueryStatus =>
  v !== null &&
  Object.values<string>(SearchInvoiceReportsQueryStatus).includes(v);

const isValidPaymentStatus = (
  v: string | null
): v is SearchInvoiceReportsQueryPaymentStatus =>
  v !== null &&
  Object.values<string>(SearchInvoiceReportsQueryPaymentStatus).includes(v);

const isValidMfFile = (
  v: string | null
): v is SearchInvoiceReportsQueryMfFile =>
  v !== null &&
  Object.values<string>(SearchInvoiceReportsQueryMfFile).includes(v);

const isValidPaymentMethod = (
  v: string | null
): v is SearchAdminInvoiceReportsQueryPaymentMethod =>
  v !== null &&
  Object.values<string>(SearchAdminInvoiceReportsQueryPaymentMethod).includes(
    v
  );

export const useAdminInvoiceReportsFormInitial =
  (): AdminInvoiceReportsSearchFormData => {
    const location = useLocation();
    const { getDateParam, getArrayParam } = useQueryParams();

    return useMemo(() => {
      const searchParams = new URLSearchParams(location.search);
      const page = Number(searchParams.get('page')) || 1;
      const sortFields = searchParams.get('sort_fields') || undefined;
      const sortOrders = searchParams.get('sort_orders') || undefined;

      const rawStatus = searchParams.get('status');
      const rawPaymentStatus = searchParams.get('payment_status');
      const rawMfFile = searchParams.get('mf_file');
      const rawPaymentMethod = searchParams.get('payment_method');

      return {
        query: searchParams.get('query') ?? '',
        number: searchParams.get('number') ?? '',
        status: isValidStatus(rawStatus) ? rawStatus : null,
        payment_status: isValidPaymentStatus(rawPaymentStatus)
          ? rawPaymentStatus
          : null,
        mf_file: isValidMfFile(rawMfFile) ? rawMfFile : null,
        ap_payee_ids: getArrayParam(searchParams, 'ap_payee_ids') ?? [],
        applicant_ids: getArrayParam(searchParams, 'office_member_ids') ?? [],
        applicant_department_ids: getArrayParam(searchParams, 'dept_ids') ?? [],
        department_ids: getArrayParam(searchParams, 'report_dept_ids') ?? [],
        ap_counterparty_ids:
          getArrayParam(searchParams, 'ap_counterparty_ids') ?? [],
        payment_method: isValidPaymentMethod(rawPaymentMethod)
          ? rawPaymentMethod
          : null,
        total_amount_from: searchParams.get('total_amount_from') ?? '',
        total_amount_to: searchParams.get('total_amount_to') ?? '',
        created_date: [
          getDateParam(searchParams, 'created_at_from'),
          getDateParam(searchParams, 'created_at_to'),
        ],
        submitted_date: [
          getDateParam(searchParams, 'submitted_at_from'),
          getDateParam(searchParams, 'submitted_at_to'),
        ],
        approved_date: [
          getDateParam(searchParams, 'approved_at_from'),
          getDateParam(searchParams, 'approved_at_to'),
        ],
        book_date: [
          getDateParam(searchParams, 'book_date_from'),
          getDateParam(searchParams, 'book_date_to'),
        ],
        due_date: [
          getDateParam(searchParams, 'due_date_from'),
          getDateParam(searchParams, 'due_date_to'),
        ],
        payment_date: [
          getDateParam(searchParams, 'payment_date_from'),
          getDateParam(searchParams, 'payment_date_to'),
        ],
        sort_fields: sortFields ? [sortFields] : undefined,
        sort_orders: sortOrders ? [sortOrders] : undefined,
        page,
      };
    }, [location.search, getDateParam, getArrayParam]);
  };
